<template>
  <div class="report-page">
    <div class="filter-card">
      <div class="fc-row fc-row-primary">
        <div class="field">
          <label class="field-lbl">ช่วงวันที่ (วันที่สร้าง)</label>
          <date-picker
            v-model="dateRange"
            type="date"
            range
            format="DD MMM YY"
            :shortcuts="dateRangeShortcuts"
            :disabled-date="notAfterToday"
            placeholder="เลือกวันที่ต้องการค้นหา"
            input-class="form-control form-control-sm field-input-native"
          ></date-picker>
        </div>

        <div class="field">
          <label class="field-lbl">แพทย์</label>
          <b-form-select
            v-model="doctorFilter"
            :options="doctorOptions"
            size="sm"
            class="field-input-native"
          ></b-form-select>
        </div>

        <b-button variant="primary" class="btn-primary-lg" @click="fetchReport">
          แสดงข้อมูล
        </b-button>
      </div>

      <div class="fc-row fc-row-multi">
        <ClinicBranchMultiSelect
          :value="branchFilter"
          :options="branchOptions"
          @input="$emit('update:branchFilter', $event)"
        />
        <ClinicItemMultiSelect
          v-model="labFilter"
          :items="labOptions"
          label="รายการแลป"
          placeholder="ทุกรายการ · พิมพ์ชื่อหรือรหัสงานแลปเพื่อค้นหา"
        />
      </div>
    </div>

    <div id="report-elements">
      <ClinicBranchSummary :rows="filteredList" :metrics="summaryMetrics" />

      <div class="toolbar">
        <b-pagination
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          size="sm"
          first-number
          last-number
          pills
          limit="10"
          class="mb-0"
        ></b-pagination>

        <div class="toolbar-right">
          <b-dropdown size="sm" variant="outline-success" right>
            <template #button-content>
              <i class="fas fa-download"></i>
            </template>
            <b-dropdown-item @click="excelExport('ClinicLabTable')">
              <i class="fas fa-file-excel mr-2"></i>Excel
            </b-dropdown-item>
            <b-dropdown-item @click="pdfExport('ClinicLabTable')">
              <i class="fas fa-file-pdf mr-2"></i>PDF
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <b-table
        id="ClinicLabTable"
        ref="ClinicLabTable"
        class="my-0 sticky-footer report-table"
        small
        sort-icon-left
        hover
        head-variant="light"
        :busy="tableBusy"
        :items="filteredList"
        :fields="fields"
        sort-by="branchName"
        :responsive="true"
        show-empty
        :per-page="perPage"
        :current-page="currentPage"
      >
        <template #cell(index)="row">{{ row.index + 1 }}.</template>
        <template #cell(creationDt)="row">{{ formatDate(row.value) }}</template>
        <template #custom-foot>
          <b-tr variant="secondary" class="text-bold text-right">
            <td v-for="field in fields" :key="field.key">
              {{ isSumField(field.key) ? formatNumber(sum(field.key)) : "" }}
            </td>
          </b-tr>
        </template>
        <template #empty="">
          <p class="text-center text-muted my-2">ไม่มีข้อมูลให้แสดง</p>
        </template>
      </b-table>
    </div>

    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import ClinicBranchSummary from "@/components/reports/ClinicBranchSummary";
import ClinicBranchMultiSelect from "@/components/reports/ClinicBranchMultiSelect";
import ClinicItemMultiSelect from "@/components/reports/ClinicItemMultiSelect";
import moment from "moment";
import {
  branchNameOptions,
  matchesBranches,
  labItemOption,
  uniqueItemOptions,
  selectedItemKeys,
} from "@/utils/clinicReportFilters";

import { exportTableToExcel, exportReportToPdf } from "@/utils/reportExport";

export default {
  name: "ClinicLabReport",
  // NOTE: click-to-view invoice photos is intentionally NOT here — it is deferred
  // to a separate session (see .superpower/clinic-income-report/handoff-lab-photo.md).
  // Rows already carry branchUrl so that work can wire the media fetch per row.
  components: {
    Dialog,
    Loading,
    ClinicBranchSummary,
    ClinicBranchMultiSelect,
    ClinicItemMultiSelect,
  },
  props: {
    // Selected branch names, shared with the other clinic report tabs. Empty = all.
    branchFilter: { type: Array, default: () => [] },
  },
  data() {
    return {
      isLoading: false,
      reportTitle: "รายงานค่าใช้จ่ายแลป (คลินิก)",
      reportNameEn: "Clinic-LabCost",

      dateRange: [],
      doctorFilter: null,
      // { type: "item", key, label } | { type: "keyword", keyword }. Empty = all lab items.
      labFilter: [],

      tableBusy: false,
      reportList: [],
      fields: [
        { key: "index", label: "" },
        { key: "branchName", label: "สาขา", sortable: true, class: "narrow-spacing" },
        { key: "creationDt", label: "วันที่สร้าง", sortable: true, class: "narrow-spacing" },
        { key: "invoiceNo", label: "เลขที่ invoice", class: "narrow-spacing", tdAttr: { "data-t": "s" } },
        {
          key: "labCode",
          label: "รหัสแลป",
          class: "narrow-spacing",
          tdAttr: { "data-t": "s" },
          formatter: (v, key, item) => (item.mLab && item.mLab.code) || "",
        },
        {
          key: "mLab",
          label: "งานแลป",
          class: "narrow-spacing",
          formatter: i => (i && i.nameTh ? i.nameTh : ""),
        },
        {
          key: "mCompanyLab",
          label: "ชื่อบริษัท",
          class: "narrow-spacing",
          formatter: i => (i && i.nameTh ? i.nameTh : ""),
        },
        {
          key: "patient",
          label: "ชื่อคนไข้",
          class: "narrow-spacing",
          formatter: i => (i ? `${i.firstNameTh || ""} ${i.lastNameTh || ""}`.trim() : ""),
        },
        {
          key: "doctor",
          label: "ชื่อแพทย์",
          class: "narrow-spacing",
          formatter: i => (i && i.fullName ? i.fullName : ""),
        },
        { key: "dfAmount", label: "DF", class: "text-right", formatter: "formatNumber" },
        { key: "cfAmount", label: "CF", class: "text-right", formatter: "formatNumber" },
        { key: "price", label: "รวม", class: "text-right", formatter: "formatNumber" },
      ],
      sumKeys: ["dfAmount", "cfAmount", "price"],
      summaryMetrics: [
        { key: "dfAmount", label: "DF" },
        { key: "cfAmount", label: "CF" },
        { key: "price", label: "รวม" },
      ],

      currentPage: 1,
      perPage: 100,
    };
  },
  computed: {
    ...mapGetters({
      getReportDateRange: "moduleReport/getReportDateRange",
      dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts",
      clinicInfo: "moduleBranch/getClinicInfo",
    }),
    branchOptions() {
      return branchNameOptions(this.reportList);
    },
    labOptions() {
      return uniqueItemOptions(this.reportList.map(labItemOption));
    },
    doctorOptions() {
      const map = new Map();
      for (const r of this.reportList) {
        if (r.uid != null && r.doctor && r.doctor.fullName) {
          map.set(r.uid, r.doctor.fullName);
        }
      }
      const docs = [...map.entries()].sort((a, b) =>
        String(a[1]).localeCompare(String(b[1]), "th")
      );
      return [
        { value: null, text: "ทุกแพทย์" },
        ...docs.map(([uid, name]) => ({ value: uid, text: name })),
      ];
    },
    filteredList() {
      const labKeys = selectedItemKeys(this.labFilter, this.labOptions);
      return this.reportList
        .filter(i => matchesBranches(i.branchName, this.branchFilter))
        .filter(i => {
          if (!this.labFilter.length) return true;
          const option = labItemOption(i);
          return !!option && labKeys.has(option.key);
        })
        .filter(i => (this.doctorFilter != null ? i.uid === this.doctorFilter : true))
        .slice(0)
        .sort((a, b) => {
          const byBranch = String(a.branchName || "").localeCompare(
            String(b.branchName || ""),
            "th"
          );
          if (byBranch !== 0) return byBranch;
          return moment(a.creationDt).valueOf() - moment(b.creationDt).valueOf();
        })
        .map(i => ({
          ...i,
          dfAmount: Number(i.dfAmount) || 0,
          cfAmount: Number(i.cfAmount) || 0,
          price: Number(i.price) || 0,
        }));
    },
    today() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
    rows() {
      return this.filteredList.length;
    },
  },
  watch: {
    branchFilter() {
      this.currentPage = 1;
    },
    doctorFilter() {
      this.currentPage = 1;
    },
    labFilter() {
      this.currentPage = 1;
    },
  },
  methods: {
    ...mapActions({
      fetchClinicLabExpense: "moduleReport/fetchClinicLabExpense",
      setStateReportDateRange: "moduleReport/setStateReportDateRange",
    }),
    moment() {
      return moment();
    },
    formatDate(date) {
      return date ? moment(date).locale("th").format("D MMM YY") : "";
    },
    formatNumber(value) {
      if (value === null || value === undefined || isNaN(value)) return "";
      return Number(value).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    isSumField(key) {
      return this.sumKeys.includes(key);
    },
    sum(key) {
      if (!this.isSumField(key)) return null;
      return this.filteredList.reduce((accum, item) => {
        const data = item[key];
        if (typeof data != "number") return accum;
        return Math.round((accum + data) * 20) / 20;
      }, 0);
    },
    formatStartDate(date) {
      return date ? moment(date).startOf("day").format() : null;
    },
    formatEndDate(date) {
      return date ? moment(date).endOf("day").format() : null;
    },
    fetchReport() {
      if (!this.dateRange[0] || !this.dateRange[1]) return;
      this.setStateReportDateRange(this.dateRange);

      this.isLoading = true;
      this.fetchClinicLabExpense({
        clinicUrl: this.$route.params.clinicUrl,
        event: {
          fromCreationDate: this.formatStartDate(this.dateRange[0]),
          toCreationDate: this.formatEndDate(this.dateRange[1]),
        },
      })
        .then(res => {
          this.reportList = res.data;
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    async excelExport(id) {
      this.perPage = null;
      await this.$nextTick();
      exportTableToExcel({
        tableId: id,
        reportTitle: this.reportTitle,
        reportNameEn: this.reportNameEn,
        dateFrom: this.dateRange[0],
        dateTo: this.dateRange[1],
        clinicName: this.clinicInfo && this.clinicInfo.name,
      });
      this.perPage = 100;
    },
    async pdfExport(id) {
      this.perPage = null;
      await this.$nextTick();
      exportReportToPdf({
        tableId: id,
        reportTitle: this.reportTitle,
        reportNameEn: this.reportNameEn,
        dateFrom: this.dateRange[0],
        dateTo: this.dateRange[1],
        clinicName: this.clinicInfo && this.clinicInfo.name,
      });
      this.perPage = 100;
    },
    notAfterToday(date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0));
    },
  },
  mounted() {
    if (this.getReportDateRange[0] && this.getReportDateRange[1]) {
      this.dateRange = this.getReportDateRange;
    } else {
      this.dateRange = [this.today, this.today];
    }
  },
};
</script>

<style scoped></style>
